import { computed, ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { kbAssistantProposalService } from '@/api/services/kbAssistantProposal.service'
import type {
  KbAssistantProposal,
  KbAssistantProposalAcceptResult,
  KbAssistantProposalCounts,
  KbAssistantProposalUpdatePayload,
} from '@/types/kbAssistantProposal.types'

/**
 * Pinia store for the KB Assistant proposals.
 *
 * It holds **no paginated list**. The paginated queue was removed on 2026-09-09
 * when review moved entirely into the Knowledge Base editor: the KB Assistant
 * page became a summary fed by `/counts`, and the editor fetches either one
 * proposal by id or the small "in attesa" side-lists. Nothing renders a page of
 * proposals any more, so `items` / `fetchItems` / the paging refs went with it
 * rather than staying as an unused `useListView` shell.
 *
 * **The counts half is the reason this store exists.** Three separate places
 * render a badge from the same data — the sidebar total, the per-topic bells on
 * the Knowledge Base list, and the per-paragraph bells in the editor. Holding
 * one `counts` object here means one HTTP call feeds all three and they can
 * never disagree; letting each component fetch its own would guarantee they
 * eventually would.
 */
export const useKbAssistantProposalStore = defineStore('kbAssistantProposal', () => {
  // ── Shared error channel ──────────────────────────────────────────────
  // Read by the dashboard and by the editor's review panel.
  const error = ref<string | null>(null)

  // ── Counts state (shared by every badge site) ─────────────────────────
  const counts = ref<KbAssistantProposalCounts>({
    total_pending: 0,
    unassigned_pending: 0,
    by_topic: {},
    by_paragraph: {},
    // The PENDING-CAPABLE kinds only (2026-09-23). The other five are applied
    // in the creating transaction and can never be pending, so carrying them
    // would put permanently-zero rows on a dashboard that reads a zero as "none
    // open" — a true statement about a thing that cannot happen.
    by_kind: { paragraph_update: 0, paragraph_delete: 0, delete_topic: 0 },
    review_paragraphs_by_topic: {},
  })

  // ── Detail state (the review panel / queue detail) ────────────────────
  const current = ref<KbAssistantProposal | null>(null)
  const currentLoading = ref(false)
  /** Set while accept/reject/edit is in flight, so the panel can disable its
   *  buttons — a double-click on Accetta is a `409`, not a silent no-op. */
  const submitting = ref(false)

  /* Three refs lived here until 2026-09-15: `pendingNewTopics`,
     `pendingNewParagraphs` and `pendingChildren`, plus `promotedChildCount`.
     They fed the editor's dashed "in attesa di revisione" blocks and the
     message that followed accepting a proposed topic. `new_topic` and
     `new_paragraph` are now accepted at creation, so nothing is rowless and
     there is nothing to list. */

  /** The sidebar badge. */
  const totalPending = computed(() => counts.value.total_pending)

  /** Bell count for one topic: how many of its paragraphs a reviewer has to
   *  open — a pending rewrite or delete, or a text nobody has confirmed (an
   *  auto-accepted new paragraph, say). Plus 1 for a pending topic deletion. */
  function topicPendingCount(topicId: string): number {
    return counts.value.review_paragraphs_by_topic[topicId] ?? 0
  }

  // ── Pending proposals of the open topic (the paragraph bells) ─────────
  /** Keyed by paragraph id. Only the two kinds that sit on a paragraph and wait
   *  for a verdict; the bell itself is computed by `paragraphBellCount`, from
   *  these and the paragraph's own text. */
  const topicPending = ref<Record<string, KbAssistantProposal>>({})
  let topicPendingId: string | null = null
  let topicPendingTicket = 0

  async function fetchTopicPending(topicId: string | null): Promise<void> {
    topicPendingId = topicId
    topicPendingTicket += 1
    const ticket = topicPendingTicket
    if (topicId === null) {
      topicPending.value = {}
      return
    }
    try {
      const data = await kbAssistantProposalService.list({
        topic_id: topicId,
        kind: ['paragraph_update', 'paragraph_delete'],
        status: ['pending'],
        page_size: 200,
      })
      if (ticket !== topicPendingTicket) return
      const byParagraph: Record<string, KbAssistantProposal> = {}
      for (const proposal of data.items) {
        if (proposal.paragraph_id !== null) byParagraph[proposal.paragraph_id] = proposal
      }
      topicPending.value = byParagraph
    } catch {
      // A missing bell is recoverable on the next refresh; the editor itself
      // still loads the proposal when the paragraph is opened.
      if (ticket === topicPendingTicket) topicPending.value = {}
    }
  }

  /** Re-read everything a verdict changes: the counts and the open topic's bells. */
  async function refreshAfterVerdict(): Promise<void> {
    await Promise.all([fetchCounts(), fetchTopicPending(topicPendingId)])
  }

  /**
   * Refresh the badge counts.
   *
   * Always unscoped. The endpoint's `topic_id` param scopes the **whole**
   * response, `total_pending` included, so a scoped fetch must never be written
   * into this shared object — the sidebar badge would then show one topic's
   * count instead of the KB's.
   */
  async function fetchCounts() {
    try {
      counts.value = await kbAssistantProposalService.counts()
      error.value = null
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to load proposal counts'
    }
  }

  /**
   * The id of the most recent detail request.
   *
   * Clearing `current` *before* a fetch stops the previous proposal from
   * lingering on screen, but it does not order the responses. Click paragraph A
   * then quickly B: if A resolves last, `current` ends up holding A's proposal
   * while B is open, and Accetta would write to the wrong paragraph. Every
   * resolver therefore checks it still owns the latest request before assigning.
   */
  const detailRequestId = ref(0)

  async function fetchOne(id: string) {
    detailRequestId.value += 1
    const ticket = detailRequestId.value
    currentLoading.value = true
    try {
      const loaded = await kbAssistantProposalService.getById(id)
      if (ticket !== detailRequestId.value) return
      current.value = loaded
      error.value = null
    } catch (e: unknown) {
      if (ticket !== detailRequestId.value) return
      error.value = e instanceof Error ? e.message : 'Failed to load the proposal'
      current.value = null
    } finally {
      if (ticket === detailRequestId.value) currentLoading.value = false
    }
  }

  /**
   * The paragraph's open suggestion, if any — the editor panel's data source.
   *
   * Returns `null` rather than throwing when there is none, since "no pending
   * proposal" is the normal case for most paragraphs.
   */
  async function fetchForParagraph(paragraphId: string): Promise<KbAssistantProposal | null> {
    detailRequestId.value += 1
    const ticket = detailRequestId.value
    currentLoading.value = true
    try {
      const data = await kbAssistantProposalService.list({
        paragraph_id: paragraphId,
        status: ['pending'],
        page_size: 1,
      })
      if (ticket !== detailRequestId.value) return null
      const found = data.items[0] ?? null
      if (found === null) {
        current.value = null
        error.value = null
        return null
      }
      // Re-fetch the detail: the list response omits the diff block and the
      // full case list, and the panel needs both.
      const loaded = await kbAssistantProposalService.getById(found.id)
      if (ticket !== detailRequestId.value) return null
      current.value = loaded
      error.value = null
      return loaded
    } catch (e: unknown) {
      // This was the one fetch in the store with no `try`/`catch`, and its only
      // caller invokes it as `void fetchForParagraph(...)` from a watcher — so a
      // failed list request was an unhandled rejection with no UI feedback.
      if (ticket !== detailRequestId.value) return null
      error.value = e instanceof Error ? e.message : 'Failed to load the proposal'
      current.value = null
      return null
    } finally {
      if (ticket === detailRequestId.value) currentLoading.value = false
    }
  }

  /**
   * The open `delete_topic` proposal for one topic, or `null`.
   *
   * A SEPARATE ref from `current`, and it has to be: `current` is keyed to the
   * open paragraph and a `delete_topic` has no paragraph at all, so the two can
   * legitimately be non-null at the same time — a topic proposed for deletion
   * while the reviewer has one of its paragraphs open. Reusing one ref would
   * make whichever fetch finished last win.
   *
   * `kbap_one_pending_per_topic` guarantees at most one, so `items[0]` is the
   * answer and not a first-of-many.
   */
  const currentTopicDeletion = ref<KbAssistantProposal | null>(null)

  async function fetchTopicDeletion(topicId: string): Promise<KbAssistantProposal | null> {
    try {
      const data = await kbAssistantProposalService.list({
        topic_id: topicId,
        kind: ['delete_topic'],
        status: ['pending'],
        page_size: 1,
      })
      const found = data.items[0] ?? null
      if (found === null) {
        currentTopicDeletion.value = null
        return null
      }
      // The list omits the diff block, and `target_paragraph_count` — the live
      // count the reviewer is being asked to agree to lose — lives in it.
      currentTopicDeletion.value = await kbAssistantProposalService.getById(found.id)
      return currentTopicDeletion.value
    } catch {
      // A missing topic-level proposal is the normal case for nearly every
      // topic, so a failure here must not blank the panel it decorates.
      currentTopicDeletion.value = null
      return null
    }
  }

  /**
   * The accepted proposal that created this paragraph, if the assistant wrote
   * it — for the date on the editor's "KB Assistant" tag and for the "Vedi i N
   * casi in Escalations" button. Both kinds that create a paragraph are
   * auto-accepted, so `fetchForParagraph` (pending only) never sees them.
   *
   * The list has no filter on `resulting_paragraph_id`, so it matches locally,
   * and only for a paragraph whose text is still unconfirmed. Two lists, because
   * the kinds sit in different places: `new_paragraph` carries the topic's id,
   * while `new_topic` belongs to no existing topic and is read unscoped. Newest
   * first, so the 200-row page reaches the recent, still-unconfirmed ones.
   *
   * Re-fetched by id: list responses omit `source_case_ids`, and the button
   * needs them to build its filter.
   */
  async function fetchCreationProposal(
    paragraphId: string,
    topicId: string,
  ): Promise<KbAssistantProposal | null> {
    try {
      const [inTopic, newTopics] = await Promise.all([
        kbAssistantProposalService.list({
          topic_id: topicId,
          kind: ['new_paragraph'],
          status: ['accepted'],
          page_size: 200,
        }),
        kbAssistantProposalService.list({
          kind: ['new_topic'],
          status: ['accepted'],
          page_size: 200,
        }),
      ])
      const found = [...inTopic.items, ...newTopics.items].find(
        (p) => p.resulting_paragraph_id === paragraphId,
      )
      if (found === undefined) return null
      return await kbAssistantProposalService.getById(found.id)
    } catch {
      return null
    }
  }

  async function updateProposal(id: string, payload: KbAssistantProposalUpdatePayload) {
    submitting.value = true
    try {
      current.value = await kbAssistantProposalService.update(id, payload)
      return current.value
    } finally {
      submitting.value = false
    }
  }

  /**
   * Accept.
   *
   * Takes **no lock of its own**. Every caller reaches this from the Knowledge
   * Base editor, which already holds the paragraph lock for the open paragraph;
   * acquiring a second one here would be pointless, and releasing it afterwards
   * would drop the lock out from under the editing session.
   *
   * The acquire-then-release branch this used to carry existed for the paginated
   * queue page, which had no lock — that page was replaced by a summary on
   * 2026-09-09 and the branch lost its only caller. Left in place it would have
   * been an untested path that merely looked supported. If a future surface ever
   * needs to accept a `paragraph_update` without holding the lock, the backend
   * says so plainly with `409 lock-required`.
   *
   * The two create kinds (`new_paragraph`, `new_topic`) never needed a lock:
   * there is no existing paragraph to lock.
   *
   * Errors are **re-thrown**, not flattened into `error`: the caller has to tell
   * `409 proposal-stale` (retry with `acknowledge_stale`) from
   * `409 lock-conflict` (someone else is editing) from `409 title-collision`
   * (edit the title), and each needs a different remedy.
   */
  async function acceptProposal(
    id: string,
    acknowledgeStale = false,
    comment?: string | null,
  ): Promise<KbAssistantProposalAcceptResult> {
    submitting.value = true
    try {
      const result = await kbAssistantProposalService.accept(id, {
        acknowledge_stale: acknowledgeStale,
        comment: comment ?? null,
      })
      // The verdict changed the pending set — every badge is now stale.
      await refreshAfterVerdict()
      current.value = null
      return result
    } finally {
      submitting.value = false
    }
  }

  async function rejectProposal(id: string, reason?: string | null) {
    submitting.value = true
    try {
      const rejected = await kbAssistantProposalService.reject(id, reason)
      await refreshAfterVerdict()
      current.value = null
      return rejected
    } finally {
      submitting.value = false
    }
  }

  return {
    currentTopicDeletion,
    fetchTopicDeletion,
    error,
    counts,
    totalPending,
    current,
    currentLoading,
    submitting,
    topicPendingCount,
    topicPending,
    fetchTopicPending,
    fetchCreationProposal,
    fetchCounts,
    fetchOne,
    fetchForParagraph,
    updateProposal,
    acceptProposal,
    rejectProposal,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useKbAssistantProposalStore, import.meta.hot))
}
